/** Utility helpers shared across the app. */

/**
 * Current market session (client-side, Eastern time): 'premarket' | 'regular' | 'afterhours' | 'closed'.
 * Mirrors shared/market_hours.py's get_session() boundaries (4:00 AM-8:00 PM ET, Mon-Fri).
 * Prefer the backend's /api/health `market_session` field when available — this is a
 * same-boundary client-side fallback for immediate display before that response lands.
 */
export function getMarketSession() {
  try {
    const now = new Date()
    const et = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }))
    const day = et.getDay() // 0=Sun, 6=Sat
    if (day === 0 || day === 6) return 'closed'
    const mins = et.getHours() * 60 + et.getMinutes()
    if (mins >= 240 && mins < 570) return 'premarket'   // 4:00 AM - 9:30 AM
    if (mins >= 570 && mins < 960) return 'regular'     // 9:30 AM - 4:00 PM
    if (mins >= 960 && mins < 1200) return 'afterhours' // 4:00 PM - 8:00 PM
    return 'closed'
  } catch {
    return 'closed'
  }
}

/** Is the US stock market currently in its regular session? (client-side, Eastern time) */
export function isMarketOpen() {
  return getMarketSession() === 'regular'
}

/** Current Eastern time as HH:MM string. */
export function getETTime() {
  try {
    return new Date().toLocaleTimeString('en-US', {
      timeZone: 'America/New_York',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
  } catch {
    return '--:--'
  }
}

/** Format a UTC/ISO timestamp as a relative string ("2m ago"). */
export function timeAgo(isoStr) {
  if (!isoStr) return '—'
  const diff = (Date.now() - new Date(isoStr)) / 1000
  if (diff < 60) return `${Math.round(diff)}s ago`
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`
  return `${Math.round(diff / 86400)}d ago`
}

/** Format number with commas: 1514095 → "1,514,095" */
export function fmtInt(n) {
  if (n === null || n === undefined) return '—'
  return Number(n).toLocaleString()
}

/** Format number with M/K suffix for large counts. */
export function fmtShort(n) {
  if (n === null || n === undefined) return '—'
  const v = Number(n)
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`
  return String(v)
}

/** Format a price: 182.451 → "$182.45" */
export function fmtPrice(n) {
  if (n === null || n === undefined) return '—'
  return `$${Number(n).toFixed(2)}`
}

/** Format a percentage: 0.85 → "85.0%" or 1.24 → "1.24%" depending on scale. */
export function fmtPct(n, alreadyPct = false) {
  if (n === null || n === undefined) return '—'
  const v = alreadyPct ? Number(n) : Number(n) * 100
  const sign = v > 0 ? '+' : ''
  return `${sign}${v.toFixed(1)}%`
}

/** Format a signal type string nicely: "rsi_oversold" → "RSI Oversold" */
export function fmtSignalType(t) {
  if (!t) return '—'
  return t.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

/** Strength bar colour class */
export function strengthColor(v) {
  const n = Number(v)
  if (n >= 0.8) return 'bg-green-500'
  if (n >= 0.6) return 'bg-blue-500'
  if (n >= 0.4) return 'bg-amber-500'
  return 'bg-red-400'
}
